<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { api, type CalendarStatus, type FundHolidayRow } from '../../api/client'
import { usePortfolio } from '../../composables/usePortfolio'
import { useVisibleFunds } from '../../composables/useVisibleFunds'
import FundNavBlock from './FundNavBlock.vue'
import PricingSourcePanel from '../PricingSourcePanel.vue'
import SwingPricingSection from './SwingPricingSection.vue'
import { GATED_FUNDS, fundForPortfolio, type Fund } from './fundConfig'

// Spec 2026-10-17 D1: NAV tab sections. Remembered per viewer; storage may throw (private window).
const SECTION_KEY = 'sw:nav-section'
type NavSection = 'comparison' | 'swing'
function readSection(): NavSection {
  try { return localStorage.getItem(SECTION_KEY) === 'swing' ? 'swing' : 'comparison' } catch { return 'comparison' }
}
const section = ref<NavSection>(readSection())
function setSection(s: NavSection) {
  section.value = s
  try { localStorage.setItem(SECTION_KEY, s) } catch { /* per-viewer convenience only */ }
}

type NavPeriod = 'T0' | 'T-1' | 'T-2' | 'T-3'
const validPeriods: NavPeriod[] = ['T0', 'T-1', 'T-2', 'T-3']

function getPeriodFromHash(): NavPeriod {
  const raw = window.location.hash.replace('#', '')
  const parts = raw.split('/')
  if (parts.length > 1) {
    const p = parts[1] as NavPeriod
    if (validPeriods.includes(p)) return p
  }
  return 'T0'
}

const activePeriod = ref<NavPeriod>(getPeriodFromHash())
const calendarData = ref<CalendarStatus | null>(null)

function getT0TradingDay(): string {
  if (calendarData.value?.tradingDay) return new Date().toISOString().slice(0, 10)
  if (calendarData.value?.nextTradingDay) return calendarData.value.nextTradingDay
  const d = new Date()
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1)
  return d.toISOString().slice(0, 10)
}

const nextTradingDay = computed(() => getT0TradingDay())
const pastTradingDays = computed(() => {
  if (calendarData.value?.pastTradingDays?.length) return calendarData.value.pastTradingDays
  const dates: string[] = []
  const d = new Date(getT0TradingDay() + 'T00:00:00')
  d.setDate(d.getDate() - 1)
  while (dates.length < 3) {
    const dow = d.getDay()
    if (dow !== 0 && dow !== 6) dates.push(d.toISOString().slice(0, 10))
    d.setDate(d.getDate() - 1)
  }
  return dates
})

const selectedDate = computed(() => {
  if (activePeriod.value === 'T0') return nextTradingDay.value
  const idx = { 'T-1': 0, 'T-2': 1, 'T-3': 2 }[activePeriod.value] ?? 0
  return pastTradingDays.value[idx]
})

const isForwardLooking = computed(() => activePeriod.value === 'T0')

function periodDateSuffix(p: NavPeriod): string {
  let date: string
  if (p === 'T0') date = nextTradingDay.value
  else {
    const idx = { 'T-1': 0, 'T-2': 1, 'T-3': 2 }[p] ?? 0
    date = pastTradingDays.value[idx]
  }
  if (!date) return ''
  const d = new Date(date + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
}

watch(activePeriod, () => {
  window.location.hash = `nav/${activePeriod.value}`
})

function onHashChange() {
  const p = getPeriodFromHash()
  if (p !== activePeriod.value) activePeriod.value = p
}

onMounted(() => {
  window.addEventListener('hashchange', onHashChange)
  api.calendarToday().then(c => { calendarData.value = c }).catch(() => {})
})

onUnmounted(() => {
  window.removeEventListener('hashchange', onHashChange)
})

// The host's own fund leads (2026-08-08 Paolo: "look at the URL, discern
// the fund from the url, display the NAV for that fund on the page") —
// jh.buxushedgefund.com must open on JH NAV, not BGA. The side-by-side
// all-funds view (PR #36) is kept; the hostname decides the order.
const { portfolio } = usePortfolio()
// Japan Phase 2 Task 8 (spec 2026-09-28): useVisibleFunds is the single
// visibility path (it already applies isFundVisibleFor across every
// REGISTERED_PORTFOLIOS entry, JAPAN included) — no second isFundVisible
// filter stacked on top of it. Final-review fix I3's rule still holds:
// entitlements not loaded ⇒ gated funds hidden, live funds shown; loaded ⇒
// exact filter. The host fund is always shown EXCEPT a gated host, which
// must still pass the predicate (a gated fund is never shown unless
// entitled) — GATED_FUNDS.includes(hostFund) is that one extra check
// visibleFunds itself can't express (it has no notion of "always show the
// current host").
const { visibleFunds } = useVisibleFunds()

// Paolo 2026-11-03: each NAV block carries a LIVE / DARK pill — LIVE when the viewer's pot allocates the fund
// (enabled, weight > 0: the FOF page's definition), DARK otherwise. The synthetic NAV itself is booked for every
// user who may SEE the fund (access, not allocation), so a DARK block still shows it.
const liveByFund = ref<Record<string, boolean>>({})
async function loadLive() {
  try {
    const r = await api.getFundAllocations()
    const rows = Array.isArray(r) ? r : (r?.allocations ?? [])
    liveByFund.value = Object.fromEntries(rows.map(a => [a.fund, !!a.enabled && (a.weightPct ?? 0) > 0]))
  } catch {
    liveByFund.value = {}   // unknown ⇒ no pill, never a wrong one
  }
}
onMounted(() => { void loadLive() })
function liveOf(f: Fund): boolean | null { return f in liveByFund.value ? liveByFund.value[f] : null }

const funds = computed<Fund[]>(() => {
  // Registry-driven (GGE promotion Phase 4): host fund first, then the
  // rest in canonical order — no hardcoded fund list to forget.
  // OTP is excluded — it's an overlay, not a fund with its own NAV
  // (same filter as StrategyAllocationCard).
  const hostFund = fundForPortfolio(portfolio.value)
  const orderedNonHost = visibleFunds.value.filter(f => f !== hostFund && f !== 'OTP')
  const hostShown = hostFund !== 'OTP' && (!GATED_FUNDS.includes(hostFund) || visibleFunds.value.includes(hostFund))
  return hostShown ? [hostFund, ...orderedNonHost] : orderedNonHost
})

// Fund → short key for funds that have a dealing calendar (spec 2026-09-07).
const CALENDAR_FUNDS: Partial<Record<Fund, string>> = { GOLD: 'gold' }
const closuresByFund = ref<Partial<Record<Fund, Record<string, string>>>>({})

function loadFundClosures() {
  for (const f of funds.value) {
    const key = CALENDAR_FUNDS[f]
    if (!key) continue
    const year = new Date().getUTCFullYear()
    Promise.all([api.calendarHolidays(key, year), api.calendarHolidays(key, year + 1).catch(() => [] as FundHolidayRow[])])
      .then(([a, b]) => {
        const map: Record<string, string> = {}
        for (const r of [...a, ...b]) if (r.fundClosure && r.fundClosureName) map[r.date] = r.fundClosureName
        closuresByFund.value = { ...closuresByFund.value, [f]: map }
      })
      .catch(() => {})
  }
}

const todayIso = () => new Date().toISOString().slice(0, 10)
function fmtShort(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z')
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', timeZone: 'UTC' })
}
// Strictly-future closures only — the fund's status pill already covers
// today's closure, so "next closed" here means upcoming, not today.
const upcomingClosures = computed(() =>
  funds.value.flatMap(f => {
    const map = closuresByFund.value[f]
    if (!map) return []
    const next = Object.keys(map).filter(d => d > todayIso()).sort().slice(0, 3)
    return next.length ? [{ fund: f, items: next.map(d => ({ date: d, label: fmtShort(d), name: map[d] })) }] : []
  }))

onMounted(loadFundClosures)
watch(funds, loadFundClosures)
</script>

<template>
  <div class="nav-panel" data-testid="nav-panel">
    <nav class="nav-sections" aria-label="NAV sections">
      <button data-testid="nav-section-comparison" :class="['nav-section-tab', { active: section === 'comparison' }]" @click="setSection('comparison')">Comparison</button>
      <button data-testid="nav-section-swing" :class="['nav-section-tab', { active: section === 'swing' }]" @click="setSection('swing')">Swing pricing</button>
    </nav>
    <template v-if="section === 'comparison'">
    <nav class="period-tabs">
      <button v-for="p in validPeriods" :key="p"
        :class="['period-tab', { active: activePeriod === p }]"
        @click="activePeriod = p">
        <span class="period-tab-code">{{ p }}</span>
        <span class="period-tab-date" v-if="periodDateSuffix(p)"> ({{ periodDateSuffix(p) }})</span>
      </button>
    </nav>
    <div v-if="upcomingClosures.length" class="dealing-calendar-strip" data-testid="dealing-calendar-strip">
      <div v-for="u in upcomingClosures" :key="u.fund" class="dcs-line">
        <span class="dcs-fund">{{ u.fund }}</span> · next closed:
        <span v-for="(it, i) in u.items" :key="it.date">{{ i ? ', ' : ' ' }}{{ it.label }}<span v-if="i === 0"> ({{ it.name }})</span></span>
      </div>
    </div>
    <div class="fund-blocks">
      <FundNavBlock
        v-for="f in funds"
        :key="f"
        :fund="f"
        :selected-date="selectedDate"
        :is-forward-looking="isForwardLooking"
        :closure-name="closuresByFund[f]?.[selectedDate] ?? null"
        :live="liveOf(f)"
      />
    </div>
    <!-- Stale-NAV pricing-source detector (spec 2026-09-25), moved here from
         Risk 2026-09-26: it measures how each fund's NAV is priced. Same
         funds as the NAV blocks above; always last. -->
    <PricingSourcePanel :visible-funds="funds" />
    </template>
    <SwingPricingSection v-else />
  </div>
</template>

<style scoped>
.nav-panel {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.period-tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #333;
}
.period-tab {
  padding: 0.5rem 0.75rem;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: #888;
  font-size: 0.8rem;
  cursor: pointer;
  margin-bottom: -2px;
  transition: color 0.15s, border-color 0.15s;
  white-space: nowrap;
}
.period-tab:hover { color: #ccc; }
.period-tab.active {
  color: #eee;
  border-bottom-color: #4caf50;
}
.nav-sections {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #333;
}
.nav-section-tab {
  padding: 0.5rem 0.75rem;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: #888;
  font-size: 0.8rem;
  cursor: pointer;
  margin-bottom: -2px;
  transition: color 0.15s, border-color 0.15s;
  white-space: nowrap;
}
.nav-section-tab:hover { color: #ccc; }
.nav-section-tab.active {
  color: #eee;
  border-bottom-color: #4caf50;
}
.fund-blocks {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.dealing-calendar-strip { font-size: 0.8rem; opacity: .8; padding: .25rem .5rem; }
.dcs-line { white-space: normal; }
.dcs-fund { font-weight: 600; }

/* Mobile-first: hide the "(Mon 24 Aug)" date suffix — the T-N code alone
   is enough context on a narrow viewport and prevents the four tabs from
   wrapping / crushing the pill row underneath. Selected date still lives
   in the FundNavBlock header (Base date + status pill). */
@media (max-width: 768px) {
  .period-tab { padding: 0.5rem 0.6rem; }
  .period-tab-date { display: none; }
}
</style>
